import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface DuplicateSuggestion {
  id: string;
  entity_type: string;
  primary_entity_id: string;
  duplicate_entity_id: string;
  match_score: number;
  reason: string | null;
  status: string;
  detected_at: string;
}

interface DuplicateGroup {
  primaryId: string;
  duplicateIds: string[];
  matchScore: number;
  reason?: string;
}

interface DeduplicationState {
  // Candidates
  candidateDuplicates: DuplicateGroup[];
  candidateScanning: boolean;
  candidateLastScanned: string | null;
  candidatePendingCount: number;
  
  // Companies
  companyDuplicates: DuplicateGroup[];
  companyScanning: boolean;
  companyLastScanned: string | null;
  companyPendingCount: number;
  
  // Skills
  skillDuplicates: Array<{ primarySkill: string; duplicates: string[]; reason: string }>;
  skillScanning: boolean;
  skillLastScanned: string | null;
  
  // Buckets
  bucketSuggestions: Array<{ primaryBucket: string; bucketsToMerge: string[]; reason: string }>;
  bucketScanning: boolean;
  bucketLastScanned: string | null;
  
  // Database suggestions (from background checks)
  dbSuggestions: DuplicateSuggestion[];
  
  // Actions
  setCandidateDuplicates: (duplicates: DuplicateGroup[]) => void;
  setCandidateScanning: (scanning: boolean) => void;
  setCandidatePendingCount: (count: number) => void;
  setCompanyDuplicates: (duplicates: DuplicateGroup[]) => void;
  setCompanyScanning: (scanning: boolean) => void;
  setCompanyPendingCount: (count: number) => void;
  setSkillDuplicates: (duplicates: Array<{ primarySkill: string; duplicates: string[]; reason: string }>) => void;
  setSkillScanning: (scanning: boolean) => void;
  setBucketSuggestions: (suggestions: Array<{ primaryBucket: string; bucketsToMerge: string[]; reason: string }>) => void;
  setBucketScanning: (scanning: boolean) => void;
  setDbSuggestions: (suggestions: DuplicateSuggestion[]) => void;
  clearAll: () => void;
}

export const useDeduplicationStore = create<DeduplicationState>()(
  persist(
    (set) => ({
      // Initial state
      candidateDuplicates: [],
      candidateScanning: false,
      candidateLastScanned: null,
      candidatePendingCount: 0,
      
      companyDuplicates: [],
      companyScanning: false,
      companyLastScanned: null,
      companyPendingCount: 0,
      
      skillDuplicates: [],
      skillScanning: false,
      skillLastScanned: null,
      
      bucketSuggestions: [],
      bucketScanning: false,
      bucketLastScanned: null,
      
      dbSuggestions: [],
      
      // Actions
      setCandidateDuplicates: (duplicates) => set({ 
        candidateDuplicates: duplicates,
        candidateLastScanned: new Date().toISOString()
      }),
      setCandidateScanning: (scanning) => set({ candidateScanning: scanning }),
      setCandidatePendingCount: (count) => set({ candidatePendingCount: count }),
      
      setCompanyDuplicates: (duplicates) => set({ 
        companyDuplicates: duplicates,
        companyLastScanned: new Date().toISOString()
      }),
      setCompanyScanning: (scanning) => set({ companyScanning: scanning }),
      setCompanyPendingCount: (count) => set({ companyPendingCount: count }),
      
      setSkillDuplicates: (duplicates) => set({ 
        skillDuplicates: duplicates,
        skillLastScanned: new Date().toISOString()
      }),
      setSkillScanning: (scanning) => set({ skillScanning: scanning }),
      
      setBucketSuggestions: (suggestions) => set({ 
        bucketSuggestions: suggestions,
        bucketLastScanned: new Date().toISOString()
      }),
      setBucketScanning: (scanning) => set({ bucketScanning: scanning }),
      
      setDbSuggestions: (suggestions) => set({ dbSuggestions: suggestions }),
      
      clearAll: () => set({
        candidateDuplicates: [],
        candidateLastScanned: null,
        candidatePendingCount: 0,
        companyDuplicates: [],
        companyLastScanned: null,
        companyPendingCount: 0,
        skillDuplicates: [],
        skillLastScanned: null,
        bucketSuggestions: [],
        bucketLastScanned: null,
        dbSuggestions: [],
      }),
    }),
    {
      name: 'deduplication-store',
    }
  )
);
